<script setup lang="ts">
import { ref } from 'vue';
import { RouterLink } from 'vue-router';
import { Menu, X } from 'lucide-vue-next';
import { SearchNavIcon, UserNavIcon } from '@/nav/lucideNavIcons';
import LanguageSelect from '@/components/LanguageSelect.vue';

defineProps<{
  searchOpen: boolean;
  searchLabel: string;
  loginLabel: string;
  variant: 'mobile-bar' | 'desktop';
  showDesktopBurger?: boolean;
  desktopNavDrawerOpen?: boolean;
}>();

defineEmits<{
  'toggle-search': [];
  'login-click': [];
  'toggle-desktop-burger': [];
}>();

/** Search button in the currently mounted toolbar (mobile vs desktop are mutually exclusive). */
const searchBtnRef = ref<HTMLButtonElement | null>(null);

defineExpose({
  searchBtnRef,
});
</script>

<template>
  <!-- Mobile / narrow header: search + sm tools + trailing slot (menu toggle) -->
  <div v-if="variant === 'mobile-bar'" class="side-nav__top-bar-actions">
    <button
      ref="searchBtnRef"
      type="button"
      class="side-nav__menu-btn side-nav__search-btn side-nav__search-btn--mobile-bar"
      :class="{ 'side-nav__search-btn--active': searchOpen }"
      :aria-label="searchLabel"
      :aria-expanded="searchOpen"
      aria-controls="side-nav-mobile-search"
      @click="$emit('toggle-search')"
    >
      <X v-if="searchOpen" class="side-nav__icon" aria-hidden="true" />
      <component v-else :is="SearchNavIcon" class="side-nav__icon" aria-hidden="true" />
    </button>
    <div class="side-nav__top-bar-sm-tools">
      <div class="side-nav__lang-wrap side-nav__lang-wrap--mobile-bar">
        <LanguageSelect class="side-nav__lang-select" variant="toolbar" />
      </div>
      <RouterLink
        to="/account"
        class="side-nav__login-btn"
        :aria-label="loginLabel"
        @click="$emit('login-click')"
      >
        <component :is="UserNavIcon" class="side-nav__login-icon" aria-hidden="true" />
      </RouterLink>
    </div>
    <slot name="trailing" />
  </div>

  <!-- Desktop: tools cluster + optional burger (burger is outside .desktop-tools in layout) -->
  <div v-else class="side-nav__desktop-end">
    <div class="side-nav__desktop-tools">
      <button
        ref="searchBtnRef"
        type="button"
        class="side-nav__menu-btn side-nav__search-btn"
        :class="{ 'side-nav__search-btn--active': searchOpen }"
        :aria-label="searchLabel"
        :aria-expanded="searchOpen"
        aria-controls="side-nav-desktop-search"
        @click="$emit('toggle-search')"
      >
        <X v-if="searchOpen" class="side-nav__icon" aria-hidden="true" />
        <component v-else :is="SearchNavIcon" class="side-nav__icon" aria-hidden="true" />
      </button>
      <div class="side-nav__lang-wrap side-nav__lang-wrap--desktop">
        <LanguageSelect class="side-nav__lang-select" variant="toolbar" />
      </div>
      <RouterLink
        to="/account"
        class="side-nav__login-btn"
        :aria-label="loginLabel"
        @click="$emit('login-click')"
      >
        <component :is="UserNavIcon" class="side-nav__login-icon" aria-hidden="true" />
      </RouterLink>
    </div>
    <button
      v-if="showDesktopBurger"
      type="button"
      class="side-nav__menu-btn side-nav__desktop-burger-btn"
      :aria-label="desktopNavDrawerOpen ? 'Close menu' : 'Open menu'"
      :aria-expanded="desktopNavDrawerOpen"
      @click="$emit('toggle-desktop-burger')"
    >
      <X v-if="desktopNavDrawerOpen" class="side-nav__icon" aria-hidden="true" />
      <Menu v-else class="side-nav__icon" aria-hidden="true" />
    </button>
  </div>
</template>
